import { useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { TrendingUp, TrendingDown, Minus, Target } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import type { MaturityLevel } from '@/features/maturity-levels/types'
import { getMaturityColorForLevel } from '@/features/maturity-levels/maturity-color-utils'

interface TrendData {
  date: string
  overall: number
  availability?: number
  control?: number
  protection?: number
  visibility?: number
  governance?: number
  [key: string]: string | number | undefined // Allow dynamic domain properties
}

interface BenchmarkItem {
  domain_name: string
  target_level: number
  description?: string | null
  domain_id?: string
}

interface TrendsComparisonProps {
  trendData: TrendData[]
  benchmarkData?: {
    industry_name?: string
    benchmarks: BenchmarkItem[]
  } | null
  assessmentName: string
  domainScores?: Record<string, { domain_score: number; maturity_level?: number }> // Current actual scores per domain
}

export function TrendsComparison({
  trendData,
  benchmarkData,
  assessmentName,
  domainScores,
}: TrendsComparisonProps) {
  const latestScore = trendData[trendData.length - 1]?.overall || 0
  const previousScore = trendData[trendData.length - 2]?.overall || 0
  const trend = latestScore - previousScore

  const getTrendIcon = (trend: number) => {
    if (trend > 1) return <TrendingUp className="h-4 w-4 text-green-600" />
    if (trend < -1) return <TrendingDown className="h-4 w-4 text-red-600" />
    return <Minus className="h-4 w-4 text-gray-600" />
  }

  const getTrendColor = (trend: number) => {
    if (trend > 1) return 'text-green-600'
    if (trend < -1) return 'text-red-600'
    return 'text-gray-600'
  }

  const { data: maturityLevelsData } = useEngineMaturityLevels()
  const levels: MaturityLevel[] = maturityLevelsData?.data ?? maturityLevelsData ?? []

  const getLevelName = (level: number) => {
    const found = levels.find(l => l.level === level)
    return found?.name ?? `Level ${level}`
  }



  const hasBenchmarks = benchmarkData && benchmarkData.benchmarks && benchmarkData.benchmarks.length > 0

  const PALETTE = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#6366f1', '#84cc16']

  // Extract domain keys from trend data
  const domainKeys = useMemo(() => {
    if (trendData.length === 0) return []
    const keys = Object.keys(trendData[0]).filter((k: string) => k !== 'date' && k !== 'overall')
    return keys
  }, [trendData])

  /** Get a stable color for a domain by its index */
  const getColor = (key: string) => PALETTE[domainKeys.indexOf(key) % PALETTE.length] || '#94a3b8'

  return (
    <div className="space-y-6">
      {/* Charts Row — two side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Maturity Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              {trendData.length >= 2 ? getTrendIcon(trend) : <TrendingUp className="h-4 w-4 text-gray-400" />}
              <span>Overall Maturity Trend</span>
            </CardTitle>
            <CardDescription>
              Overall maturity level over time for {assessmentName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length >= 2 ? (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        domain={[0, 5]}
                        tickCount={6}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v: number) => `L${v}`}
                      />
                      <Tooltip
                        formatter={(value: number) => [`Level ${value}`, 'Overall']}
                      />
                      <Line
                        type="monotone"
                        dataKey="overall"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">Latest:</span>
                    <span className="font-semibold">Level {latestScore.toFixed(0)}</span>
                  </div>
                  <div className={`flex items-center space-x-1 ${getTrendColor(trend)}`}>
                    {getTrendIcon(trend)}
                    <span>
                      {trend > 0 ? '+' : ''}{trend.toFixed(0)} level(s) from previous
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 font-medium">No trend data yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Complete multiple assessments over time to track maturity trends.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Domain Breakdown Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              <span>Domain Trends</span>
            </CardTitle>
            <CardDescription>
              Maturity level per domain over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length >= 2 && domainKeys.length > 0 ? (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        domain={[0, 5]}
                        tickCount={6}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v: number) => `L${v}`}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          `Level ${value}`,
                          name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
                        ]}
                      />
                      {domainKeys.map((key: string) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={getColor(key)}
                          strokeWidth={2}
                          dot={{ fill: getColor(key), r: 3 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="mt-3 flex flex-wrap gap-3">
                  {domainKeys.map((key: string) => (
                    <div key={key} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getColor(key) }}
                      />
                      {key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 font-medium">No domain trend data yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Complete multiple assessments to see per-domain trends.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Industry Benchmarks — Full Width */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Industry Benchmarks
          </CardTitle>
          <CardDescription>
            {hasBenchmarks
              ? `${benchmarkData.industry_name || 'Industry'} maturity targets vs. your scores`
              : 'How you compare to industry standards'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasBenchmarks ? (
            <div className="space-y-3">
              {/* Industry name badge */}
              {benchmarkData.industry_name && (
                <div className="mb-4">
                  <Badge variant="outline" className="text-sm">
                    {benchmarkData.industry_name}
                  </Badge>
                </div>
              )}

              {/* Per-domain benchmark rows */}
              {benchmarkData.benchmarks.map((bm) => {
                const domainData = domainScores?.[bm.domain_name]
                const actualLevel = domainData?.maturity_level ?? null

                return (
                  <div
                    key={bm.domain_name}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex-1 mr-6">
                      <div className="text-sm font-medium text-gray-900">{bm.domain_name}</div>
                      {bm.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{bm.description}</div>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-right">
                      {/* Target */}
                      <div>
                        <div className="text-xs text-gray-500">Target</div>
                        <div className="text-sm font-semibold text-indigo-600">
                          Level {bm.target_level} <span className="text-xs font-normal">({getLevelName(bm.target_level)})</span>
                        </div>
                      </div>

                      {/* Actual */}
                      {actualLevel !== null && (
                        <div>
                          <div className="text-xs text-gray-500">Actual</div>
                          <div className="text-sm font-semibold" style={{ color: getMaturityColorForLevel(levels, actualLevel) }}>
                            Level {actualLevel} <span className="text-xs font-normal">({getLevelName(actualLevel)})</span>
                          </div>
                        </div>
                      )}

                      {/* Status */}
                      {actualLevel !== null && (
                        <div className="w-16 text-center">
                          {actualLevel >= bm.target_level ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">Met</Badge>
                          ) : actualLevel >= bm.target_level - 1 ? (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Close</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 text-xs">Below</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Summary */}
              {domainScores && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                  {(() => {
                    const met = benchmarkData.benchmarks.filter(
                      bm => (domainScores[bm.domain_name]?.maturity_level ?? 0) >= bm.target_level
                    ).length
                    return (
                      <span>
                        <strong>{met}</strong> of {benchmarkData.benchmarks.length} benchmarks met
                      </span>
                    )
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500">
                <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 font-medium">No benchmark data available</p>
                <p className="text-sm text-gray-500 mt-1">
                  Industry benchmarks will be available once your organization's industry is configured
                  with target levels in Settings → Industries.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
